import { buildApiUrl } from "../../../config";
import { withCredentials } from "../../../services/apiClient";
import { fetchWithRetry, readApiError } from "../../../utils";

import {
  buildPathWithQuery,
  fetchSlideLibraryJson,
  isEmptyLibraryResponse,
  readJsonResponse,
  readListPayload,
} from "../http";
import { normalizeSlideLibraryCollection, normalizeSlideLibrarySlide } from "../normalizers";
import type {
  SlideLibraryCollection,
  SlideLibraryCollectionRequest,
  SlideLibraryCollectionsResponse,
  SlideLibraryCollectionSlidesRequest,
  SlideLibraryListOptions,
  SlideLibraryReorderSlidesRequest,
  SlideLibrarySlide,
  SlideLibrarySlidesResponse,
} from "../types";

export async function fetchSlideLibraryCollections(
  options: Pick<SlideLibraryListOptions, "page" | "pageSize"> = {}
): Promise<SlideLibraryCollectionsResponse> {
  const response = await fetchWithRetry(
    buildApiUrl(buildPathWithQuery("/api/finster/collections", options)),
    withCredentials({ method: "GET" }),
    1,
    "SlideLibraryCollections"
  );

  if (!response.ok) {
    if (isEmptyLibraryResponse(response)) {
      return { collections: [], totalCount: 0 };
    }
    throw new Error(await readApiError(response));
  }

  const payload = await readJsonResponse(response);
  const data = readListPayload(payload);
  const rawCollections = Array.isArray(data.collections) ? data.collections : [];
  const collections = rawCollections
    .map((collection) => normalizeSlideLibraryCollection(collection))
    .filter((collection): collection is SlideLibraryCollection => Boolean(collection));
  return {
    collections,
    totalCount: Number(data.total_count ?? data.totalCount ?? collections.length),
  };
}

export async function createSlideLibraryCollection(
  collection: SlideLibraryCollectionRequest
): Promise<SlideLibraryCollection | null> {
  const payload = await fetchSlideLibraryJson<unknown>(
    "/api/finster/collections",
    {
      method: "POST",
      body: JSON.stringify(collection),
    },
    "SlideLibraryCreateCollection"
  );
  const data = readListPayload(payload);
  return (
    normalizeSlideLibraryCollection(data.collection) ??
    normalizeSlideLibraryCollection(data) ??
    null
  );
}

export async function updateSlideLibraryCollection(
  collectionId: string,
  collection: Partial<SlideLibraryCollectionRequest>
): Promise<SlideLibraryCollection | null> {
  const payload = await fetchSlideLibraryJson<unknown>(
    `/api/finster/collections/${encodeURIComponent(collectionId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(collection),
    },
    "SlideLibraryUpdateCollection"
  );
  const data = readListPayload(payload);
  return (
    normalizeSlideLibraryCollection(data.collection) ??
    normalizeSlideLibraryCollection(data) ??
    null
  );
}

export async function deleteSlideLibraryCollection(collectionId: string): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/collections/${encodeURIComponent(collectionId)}`,
    { method: "DELETE" },
    "SlideLibraryDeleteCollection"
  );
}

export async function fetchSlideLibraryCollectionSlides(
  collectionId: string,
  options: SlideLibraryListOptions = {},
  requestOptions: Pick<RequestInit, "signal"> = {}
): Promise<SlideLibrarySlidesResponse> {
  const response = await fetchWithRetry(
    buildApiUrl(
      buildPathWithQuery(`/api/finster/collections/${encodeURIComponent(collectionId)}/slides`, options)
    ),
    withCredentials({ method: "GET", signal: requestOptions.signal }),
    1,
    "SlideLibraryCollectionSlides"
  );

  if (!response.ok) {
    if (isEmptyLibraryResponse(response)) {
      return { slides: [], totalCount: 0 };
    }
    throw new Error(await readApiError(response));
  }

  const payload = await readJsonResponse(response);
  const data = readListPayload(payload);
  const slides = Array.isArray(data.slides)
    ? data.slides
        .map((slide) => normalizeSlideLibrarySlide(slide))
        .filter((slide): slide is SlideLibrarySlide => Boolean(slide))
    : [];
  return {
    slides,
    totalCount: Number(data.total_count ?? data.totalCount ?? slides.length),
  };
}

export async function addSlidesToSlideLibraryCollection(
  collectionId: string,
  request: SlideLibraryCollectionSlidesRequest
): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/collections/${encodeURIComponent(collectionId)}/slides`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    },
    "SlideLibraryAddCollectionSlides"
  );
}

export async function reorderSlideLibraryCollectionSlides(
  collectionId: string,
  request: SlideLibraryReorderSlidesRequest
): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/collections/${encodeURIComponent(collectionId)}/slides`,
    {
      method: "PATCH",
      body: JSON.stringify(request),
    },
    "SlideLibraryReorderCollectionSlides"
  );
}

export async function removeSlidesFromSlideLibraryCollection(
  collectionId: string,
  request: SlideLibraryCollectionSlidesRequest
): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/collections/${encodeURIComponent(collectionId)}/slides`,
    {
      method: "DELETE",
      body: JSON.stringify(request),
    },
    "SlideLibraryRemoveCollectionSlides"
  );
}
